import React from "react";
import { getBackend } from "@/lib/backend/backend2";

type Props = {};

type CmdHistoryRec = {
    id: number;
    prompt: string;
    response: string[];
};

type State = {
    history: CmdHistoryRec[];
    cmd_text: string;
    command_history_idx: number;
    emitted_text: string;
};

const PROMPT_CHAR = ">";
const PRE_ID = "admin-shell-pre";
const INPUT_ID = "admin-shell-input";

export default class AdminShell extends React.Component<Props, State> {
    element: HTMLElement | null;

    constructor(props: Props) {
        super(props);
        this.element = null;
        const state: State = {
            history: [],
            cmd_text: "",
            command_history_idx: -1,
            emitted_text: "",
        };
        this.state = state;
    }

    componentDidMount() {
        this.focusOnPrompt();
    }

    componentDidUpdate() {
        this.focusOnPrompt();
    }

    focusOnPrompt() {
        const element = document.getElementById(INPUT_ID) as HTMLInputElement;
        if (element) {
            element.focus();
            element.scrollIntoView();
        }
    }
    moveCursorToEnd() {
        const element = document.getElementById(INPUT_ID) as HTMLInputElement;
        if (element) {
            const end = element.value.length;
            element.setSelectionRange(end, end);
            element.focus();
        }
    }

    appendCmdHistory(cmd: string, response: string[]): number {
        const tmp = this.state.history;
        const id = tmp.length;
        tmp.push({ id, prompt: cmd, response: response });
        this.setState({ history: tmp, command_history_idx: tmp.length, cmd_text: "" });
        return id;
    }

    async onEnterClick() {
        const cmd_text_orig = this.state.cmd_text.trim();
        if (cmd_text_orig.length > 0) {
            const lines_orig = cmd_text_orig.split("\n");

            const lines: string[] = [];
            let curr = "";
            for (const line_orig of lines_orig) {
                curr += line_orig;
                if (!curr.endsWith("\\")) {
                    lines.push(curr);
                    curr = "";
                } else {
                    curr = curr.substring(0, curr.length - 1) + "\n";
                }
            }
            if (curr.length > 0) {
                lines.push(curr);
            }

            for (const cmd_text of lines) {
                const id = this.appendCmdHistory(cmd_text, ["Executing..."]);
                try {
                    const res = await getBackend().common.adminShell({ command_line: cmd_text });

                    // inject result to history
                    const new_history = [...this.state.history];
                    new_history.filter(x => x.id == id).forEach(x => (x.response = res.result));
                    this.setState({ history: new_history });
                } catch (e) {
                    // inject result to history
                    const new_history = [...this.state.history];
                    new_history.filter(x => x.id == id).forEach(x => (x.response = ["Error: " + e]));
                    this.setState({ history: new_history });
                }
            }
        } else {
            this.appendCmdHistory("", []);
        }
    }

    onChange(event: React.ChangeEvent<HTMLTextAreaElement>): void {
        this.setState({ cmd_text: event.target.value });
    }

    handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>): boolean {
        const { key } = event;
        let idx = this.state.command_history_idx;
        let cmd_text = "";

        switch (key) {
            case "Enter":
                this.onEnterClick();
                break;
            case "ArrowUp":
                while (idx > 0) {
                    idx = idx - 1;
                    if (this.state.history[idx].prompt.trim().length != 0) {
                        break;
                    }
                }
                if (idx >= 0 && idx < this.state.history.length) {
                    cmd_text = this.state.history[idx].prompt;
                }
                this.setState({ command_history_idx: idx, cmd_text }, () => this.moveCursorToEnd());
                break;
            case "ArrowDown":
                while (idx < this.state.history.length - 1) {
                    idx = idx + 1;
                    if (this.state.history[idx].prompt.trim().length != 0) {
                        break;
                    }
                }
                if (idx >= 0 && idx < this.state.history.length) {
                    cmd_text = this.state.history[idx].prompt;
                }
                this.setState({ command_history_idx: idx, cmd_text }, () => this.moveCursorToEnd());
                break;
            default:
                return true;
        }
        return false;
    }

    createCommand(command: CmdHistoryRec, index: number): React.ReactNode {
        const { prompt, response } = command;
        const key = "_" + index;
        const text = response.join("\n");

        return (
            <div key={key}>
                <span className="p-terminal-prompt">{PROMPT_CHAR}&nbsp;</span>
                <span className="p-terminal-command">{prompt}</span>
                <div
                    className="p-terminal-response"
                    onDoubleClick={() => {
                        navigator.clipboard.writeText(text);
                        alert("Response copied");
                    }}
                >
                    {text}
                </div>
            </div>
        );
    }

    createContent(): React.ReactNode {
        const content = this.state.history.map((x, i) => this.createCommand(x, i));
        return (
            <div className="p-terminal-content" onClick={() => this.focusOnPrompt()}>
                {content}
            </div>
        );
    }

    createPromptContainer(): React.ReactNode {
        let row_count = this.state.cmd_text.split("\n").length;
        if (row_count >= 2) {
            row_count = row_count + 3;
        }
        return (
            <div className="p-terminal-prompt-container" id={PRE_ID}>
                <span className="p-terminal-prompt">{PROMPT_CHAR}&nbsp;</span>
                <textarea
                    id={INPUT_ID}
                    autoFocus
                    value={this.state.cmd_text}
                    className="p-terminal-input"
                    autoComplete="off"
                    onChange={e => this.onChange(e)}
                    spellCheck={false}
                    prefix="off"
                    rows={row_count}
                    onKeyDown={e => this.handleKeyDown(e)}
                />
            </div>
        );
    }

    render(): React.ReactNode {
        const content = this.createContent();
        const prompt = this.createPromptContainer();

        return (
            <div className="p-terminal">
                {content}
                {prompt}
            </div>
        );
    }
}
